/**
 * Gear Catalog Admin Page
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-301
 *
 * Full CRUD management interface for gear_catalog table
 * - List view with sortable columns
 * - Search and filters
 * - Pagination (50 items per page)
 * - Status toggle (activate/deactivate)
 * - Edit modal for full gear management
 * - Delete with confirmation
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Package as _Package, Loader, Package } from 'lucide-react';
import { gearCatalogAdminApi } from '../../services/catalogAdminApi';
import GearCatalogTable from '../../components/admin/GearCatalogTable';
import SearchBar from '../../components/admin/SearchBar';
import FilterDropdown from '../../components/admin/FilterDropdown';
import Pagination from '../../components/admin/Pagination';
import GearEditModal from '../../components/admin/GearEditModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function GearCatalogAdmin() {
  const navigate = useNavigate();

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Data state
  const [gear, setGear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    gearCategory: 'all',
    itemType: 'all',
    categoryL1Id: 'all',
    isActive: true
  });

  // Sorting & Pagination
  const [sortField, setSortField] = useState('gear_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal state
  const [editingGear, setEditingGear] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingGear, setDeletingGear] = useState(null);

  /**
   * Load gear from API
   */
  const loadGear = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        search: searchTerm || undefined,
        gear_category: filters.gearCategory !== 'all' ? filters.gearCategory : undefined,
        item_type: filters.itemType !== 'all' ? filters.itemType : undefined,
        category_l1_id: filters.categoryL1Id !== 'all' ? filters.categoryL1Id : undefined,
        is_active: filters.isActive,
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortOrder
      };

      const response = await gearCatalogAdminApi.listGear(params);

      setGear(response.data);
      setTotalCount(response.meta.totalCount);
      setTotalPages(response.meta.pages);
    } catch (err) {
      console.error('Failed to load gear:', err);
      setError(err.message || 'Failed to load gear catalog');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, sortField, sortOrder, currentPage]);

  /**
   * Load gear when filters change
   */
  useEffect(() => {
    loadGear();
  }, [loadGear]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setCurrentPage(1); // Reset to first page
  };

  /**
   * Handle sort changes
   */
  const handleSort = (field) => {
    if (field === sortField) {
      // Toggle order if same field
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to asc
      setSortField(field);
      setSortOrder('asc');
    }
  };

  /**
   * Handle create new gear
   */
  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingGear({
      // Default values for new gear
      item_type: 'gear',
      is_active: true
    });
  };

  /**
   * Handle edit gear
   */
  const handleEdit = (gearItem) => {
    setIsCreating(false);
    setEditingGear(gearItem);
  };

  /**
   * Handle save (create or update)
   */
  const handleSave = async (gearData) => {
    try {
      if (isCreating) {
        await gearCatalogAdminApi.createGear(gearData);
      } else {
        await gearCatalogAdminApi.updateGear(editingGear.id, gearData);
      }

      // Close modal and reload
      setEditingGear(null);
      setIsCreating(false);
      await loadGear();
    } catch (err) {
      console.error('Failed to save gear:', err);
      throw err; // Re-throw to show error in modal
    }
  };

  /**
   * Handle delete gear (soft delete)
   */
  const handleDelete = (gearItem) => {
    setDeletingGear(gearItem);
  };

  /**
   * Confirm delete
   */
  const confirmDelete = async () => {
    if (!deletingGear) {return;}

    try {
      await gearCatalogAdminApi.deleteGear(deletingGear.id);
      setDeletingGear(null);
      await loadGear();
    } catch (err) {
      console.error('Failed to delete gear:', err);
      setError(err.message || 'Failed to delete gear');
    }
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActive = async (gearId, currentStatus) => {
    try {
      // If currently active, deactivate (soft delete)
      // If currently inactive, activate
      if (currentStatus) {
        await gearCatalogAdminApi.deleteGear(gearId);
      } else {
        // Activate by updating is_active to true
        await gearCatalogAdminApi.updateGear(gearId, { is_active: true });
      }

      await loadGear();
    } catch (err) {
      console.error('Failed to toggle gear status:', err);
      setError(err.message || 'Failed to update gear status');
    }
  };

  /**
   * Handle view audit history
   */
  const handleViewAudit = (gearItem) => {
    // TODO: Open audit log modal
    console.log('View audit for:', gearItem);
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="gear"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Gear Catalog
          </h1>
          <button
            onClick={handleCreateNew}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-4)',
              border: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-white)',
              backgroundColor: 'var(--color-primary)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
              gap: 'var(--space-2)'
            }}
          >
            <Plus style={{ width: '16px', height: '16px' }} />
            Add Gear
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Search & Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-1">
              <SearchBar
                onSearch={setSearchTerm}
                placeholder="Search gear by name..."
              />
            </div>

            {/* Gear Category Filter */}
            <FilterDropdown
              label="Gear Category"
              value={filters.gearCategory}
              onChange={(value) => handleFilterChange('gearCategory', value)}
              options={[
                { value: 'all', label: 'All Categories' },
                { value: 'Personal Gear', label: 'Personal Gear' },
                { value: 'Technical Equipment', label: 'Technical Equipment' },
                { value: 'Safety Equipment', label: 'Safety Equipment' },
                { value: 'Navigation', label: 'Navigation' },
                { value: 'Shelter & Sleeping', label: 'Shelter & Sleeping' },
                { value: 'Food & Water', label: 'Food & Water' },
                { value: 'Clothing', label: 'Clothing' },
                { value: 'Transportation', label: 'Transportation' },
                { value: 'Specialized Equipment', label: 'Specialized Equipment' },
                { value: 'Other', label: 'Other' }
              ]}
            />

            {/* Item Type Filter */}
            <FilterDropdown
              label="Item Type"
              value={filters.itemType}
              onChange={(value) => handleFilterChange('itemType', value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'gear', label: 'Gear' },
                { value: 'other', label: 'Other' }
              ]}
            />

            {/* Active Status Filter */}
            <FilterDropdown
              label="Status"
              value={filters.isActive}
              onChange={(value) => handleFilterChange('isActive', value === 'true')}
              options={[
                { value: true, label: 'Active Only' },
                { value: false, label: 'Inactive Only' },
                { value: 'all', label: 'All Statuses' }
              ]}
            />
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            {loading ? (
              <span>Loading...</span>
            ) : (
              <span>
                Showing {gear.length} of {totalCount} gear items
              </span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-4 text-gray-600">Loading gear catalog...</p>
          </div>
        )}

        {/* Gear Table */}
        {!loading && gear.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <GearCatalogTable
              gear={gear}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              onViewAudit={handleViewAudit}
            />

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && gear.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No gear found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchTerm || filters.gearCategory !== 'all' || filters.itemType !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first gear item'}
            </p>
            {!searchTerm && filters.gearCategory === 'all' && (
              <button
                onClick={handleCreateNew}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Gear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingGear && (
        <GearEditModal
          gear={editingGear}
          isCreating={isCreating}
          onSave={handleSave}
          onClose={() => {
            setEditingGear(null);
            setIsCreating(false);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingGear && (
        <ConfirmDialog
          title="Deactivate Gear"
          message={`Are you sure you want to deactivate "${deletingGear.gear_name}"? This will mark it as inactive and hide it from guides.`}
          confirmLabel="Deactivate"
          confirmClass="bg-red-600 hover:bg-red-700"
          onConfirm={confirmDelete}
          onCancel={() => setDeletingGear(null)}
        />
      )}
    </DashboardLayout>
  );
}
